import React, { useEffect, useState } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import Presenter from './Presenter';
import { IContainerProps, IBookingDetails } from './types';
import { BOOKING_STATUS } from '../../utils/constants';

const API_URL = process.env.REACT_APP_PLATFORM_API_BASE_URL;

interface RouteParams {
    bookingId: string;
}

const CancelBookingContainer = (props: IContainerProps) => {
    const [isLoading, setIsLoading] = useState(true);
    const [booking, setBooking] = useState<IBookingDetails | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [isCancelling, setIsCancelling] = useState(false);
    const [isCancelled, setIsCancelled] = useState(false);

    const { bookingId } = useParams<RouteParams>();
    const history = useHistory();
    const location = useLocation();

    // Get email from query params
    const queryParams = new URLSearchParams(location.search);
    const email = queryParams.get('email');

    useEffect(() => {
        const fetchBookingDetails = async () => {
            if (!bookingId) {
                setError('ID de reserva no encontrado');
                setIsLoading(false);
                return;
            }

            if (!email) {
                setError('Email no encontrado en la URL');
                setIsLoading(false);
                return;
            }

            try {
                setIsLoading(true);
                const encodedEmail = encodeURIComponent(email.toLowerCase());

                // Fetch user bookings and find the specific one
                const response = await fetch(`${API_URL}/api/users/${encodedEmail}/bookings`);

                if (!response.ok) {
                    throw new Error('No se pudieron obtener los detalles de la reserva');
                }

                const data = await response.json();
                const bookings = data.bookings || [];

                // Find the specific booking
                const foundBooking = bookings.find((b: any) => b.id === bookingId);

                if (!foundBooking) {
                    setError('Reserva no encontrada. Es posible que ya haya sido cancelada.');
                    setIsLoading(false);
                    return;
                }

                // Check if already cancelled
                if (foundBooking.status === BOOKING_STATUS.CANCELLED) {
                    setError('Esta reserva ya ha sido cancelada.');
                    setIsLoading(false);
                    return;
                }

                // Check if booking is in the past
                const now = Math.floor(Date.now() / 1000);
                if (foundBooking.startTimestamp && foundBooking.startTimestamp < now) {
                    setError('No se puede cancelar una reserva que ya pasó.');
                    setIsLoading(false);
                    return;
                }

                // Map to our interface
                const bookingDetails: IBookingDetails = {
                    id: foundBooking.id,
                    businessName: foundBooking.business?.name || 'Negocio',
                    businessId: foundBooking.businessId,
                    startTimestamp: foundBooking.startTimestamp,
                    selectedTime: foundBooking.selectedTime,
                    services: foundBooking.services || [],
                    totalCost: foundBooking.totalCost || 0,
                    status: foundBooking.status,
                };

                setBooking(bookingDetails);
            } catch (err) {
                console.error('Error fetching booking:', err);
                setError('Error al cargar los detalles de la reserva. Por favor intenta de nuevo.');
            } finally {
                setIsLoading(false);
            }
        };

        fetchBookingDetails();
    }, [bookingId, email]);

    const handleConfirmCancel = async () => {
        if (!bookingId || !email) {
            setError('Datos incompletos para cancelar la reserva');
            return;
        }

        try {
            setIsCancelling(true);

            const response = await fetch(`${API_URL}/api/users/bookings/${bookingId}/cancel`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ userEmail: email.toLowerCase() })
            });

            if (!response.ok) {
                const responseText = await response.text();
                let errorMsg = 'Error al cancelar la reserva';
                try {
                    const errorData = JSON.parse(responseText);
                    errorMsg = errorData.error || errorData.message || errorMsg;
                } catch {
                    errorMsg = responseText || errorMsg;
                }
                throw new Error(errorMsg);
            }

            setIsCancelled(true);
        } catch (err) {
            console.error('Error cancelling booking:', err);
            setError((err as Error).message || 'Error al cancelar la reserva');
        } finally {
            setIsCancelling(false);
        }
    };

    const handleGoHome = () => {
        history.push('/');
    };

    return (
        <Presenter
            {...props}
            isLoading={isLoading}
            booking={booking}
            error={error}
            isCancelling={isCancelling}
            isCancelled={isCancelled}
            onConfirmCancel={handleConfirmCancel}
            onGoHome={handleGoHome}
        />
    );
};

export default CancelBookingContainer;
